import { ArrowsChevronDown } from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import { Card, Loader } from "@fanduel/og-fe-library";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import ErrorComponent from "@/components/empty/ErrorComponent";
import ImageWithFallback from "@/components/Image";
import News from "@/components/svg/News";
import { Article } from "@/graphql/gql/graphql";
import { relativeDate } from "@/utils/date";
import styles from "./recentStories.module.scss";

interface RecentArticlesProps {
  articles: Article[];
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isError: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  refetch: () => void;
}

export const RecentStories = ({
  articles,
  fetchNextPage,
  hasNextPage,
  isError,
  isFetchingNextPage,
  isLoading,
  refetch,
}: RecentArticlesProps) => {
  const buttonText = "Show more news";
  const ctaButtonOnClick = useCallback(() => refetch(), [refetch]);
  const handleShowMore = useCallback(() => fetchNextPage(), [fetchNextPage]);
  const showLoadMore = useMemo(
    () => !(isError || isFetchingNextPage || isLoading) && hasNextPage,
    [isError, isFetchingNextPage, isLoading, hasNextPage]
  );
  return (
    <Card variant="elevation" elevation="low" className={styles.container}>
      <div className={styles.header}>
        <News width={20} height={21} />
        <h3>Recent Stories</h3>
      </div>
      {isError && (
        <div className={styles.loaderContainer}>
          <ErrorComponent
            subtext="Please try refreshing the module."
            ctaButtonLabel="Refresh Content"
            ctaButtonOnClick={ctaButtonOnClick}
          />
        </div>
      )}
      <div className={styles.storyContainer}>
        {articles.map((article) => (
          <div className={styles.storyItem} key={article.id}>
            <div>
              <p className={styles.articleLink}>
                <Link href={`/${article.slug}`}>{article.title}</Link>
              </p>
              <p className={styles.authorLink}>
                {article.author.slug ? (
                  <Link
                    prefetch={false}
                    href={`/author/${article.author.slug}`}
                  >
                    {article.author.name}
                  </Link>
                ) : (
                  article.author.name
                )}
                {article.firstPublishedAt ? (
                  <time
                    dateTime={article.firstPublishedAt}
                    suppressHydrationWarning
                  >
                    <span>•</span>
                    {relativeDate(article.firstPublishedAt)}
                  </time>
                ) : (
                  ""
                )}
              </p>
            </div>
            <ImageWithFallback
              width={133}
              height={77}
              src={article.thumbnailImageUrl?.url}
              alt={article.title}
            />
          </div>
        ))}
      </div>
      {(isFetchingNextPage || isLoading) && (
        <Loader size="medium" className={styles.loaderContainer} />
      )}
      {showLoadMore ? (
        <div className={styles.buttonContainer}>
          <button
            type="button"
            aria-label={buttonText}
            onClick={handleShowMore}
            className={styles.moreButton}
          >
            {buttonText}
            <ArrowsChevronDown color={fd.colors.coreBlueD1} />
          </button>
        </div>
      ) : (
        ""
      )}
    </Card>
  );
};

export default RecentStories;
